import React, { useState, useRef, useEffect } from "react";
import webbeyLogo from "../../assets/images/webbey-logo.png";
import { useWs } from "../dialer/WsWrapper";
import {
  CONNECTING,
  CONNECTED,
  BUSY,
  INIT,
} from "../../constants/connectionStatus";
import { useAuth } from "../../context/AuthContext";

const clsMap = {
  [INIT]: "init",
  [CONNECTING]: "connecting",
  [CONNECTED]: "connected",
  [BUSY]: "busy",
};

const formatSecondsToMMSS = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  // Pad with leading zeros if needed
  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(remainingSeconds).padStart(2, "0");

  return `${paddedMinutes}:${paddedSeconds}`;
};

const Header = () => {
  const { user } = useAuth();
  const {
    sinkIdAvailable,
    activeCall,
    timer,
    endCall,
    status,
    toggleMic,
    micDisabled,
    audioDevices,
    currentAudioDeviceId,
    changeMic,
    audioOutputDevices,
    currentAudioOutputDeviceId,
    changeAudioOutput,
  } = useWs();
  const [muted, setMuted] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAudioOutputDropdown, setShowAudioOutputDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const audioOutputDropdownRef = useRef(null);

  useEffect(() => {
    if (!activeCall) {
      setShowDropdown(false);
      setShowAudioOutputDropdown(false);
      setMuted(false);
    }
  }, [activeCall]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (
        audioOutputDropdownRef.current &&
        !audioOutputDropdownRef.current.contains(event.target)
      ) {
        setShowAudioOutputDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    if (showAudioOutputDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showAudioOutputDropdown]);

  const onMuteClick = () => {
    const el = document.getElementById("client-audio");
    if (el) {
      el.muted = !muted;
    }
    setMuted(!muted);
  };

  const onMicChange = (deviceId) => {
    if (deviceId !== currentAudioDeviceId) {
      changeMic(deviceId);
    }
  };

  const onAudioOutputChange = (deviceId) => {
    if (deviceId !== currentAudioOutputDeviceId) {
      changeAudioOutput(deviceId);
      setShowAudioOutputDropdown(false);
    }
  };

  const finishCall = async () => {
    if (activeCall && activeCall.callId) {
      try {
        await fetch(
          `https://callcenter-euwscall.teech.tv:3006/api/call/${activeCall.callId}`,
          {
            method: "DELETE",
          },
        );
      } catch (error) {
        console.error("Failed to delete call:", error);
      }
    }
    endCall();
  };

  let clientName;

  if (activeCall && activeCall.clientName) {
    if (activeCall.clientName.includes("[PoC] ")) {
      clientName = activeCall.clientName.split("[PoC] ")[1];
    } else {
      clientName = activeCall.clientName;
    }
  }

  return (
    <header className={!activeCall ? "header" : "header header--active-call"}>
      {!activeCall && (
        <div className="container">
          <div className="header__content">
            <div className="header__content__title">
              <img
                src={webbeyLogo}
                alt="Webbey Logo"
                className="header__logo"
              />
            </div>
            <div className="header__content__controls">
              <div className="header__content__controls__user">
                {user?.name || ""}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeCall && (
        <div className="active-call">
          <div className={`active-call__top ${clsMap[status]}`}>
            <div className="container">
              <div className="active-call__top__wrapper">
                <div className="active-call__top__user">{clientName}</div>
                <div className="active-call__top__controls">
                  <div className="active-call__top__controls__time">
                    {(status === CONNECTING || status === INIT) && "Connecting"}
                    {status === CONNECTED && formatSecondsToMMSS(timer)}
                  </div>
                  <div
                    ref={dropdownRef}
                    className={`active-call__top__controls__btn active-call__top__controls__btn--mic ${micDisabled ? "disabled" : ""}`}
                  >
                    <div
                      className="active-call__top__controls__btn__wrapper"
                      onClick={() => toggleMic()}
                    >
                      <div className="active-call__top__controls__btn__icon" />
                    </div>
                    <button
                      className="call__controls__btn-small"
                      onClick={() => setShowDropdown(!showDropdown)}
                    />
                    {showDropdown && audioDevices.length > 0 && (
                      <div className="call__controls__dropdown">
                        {audioDevices.map((device, index) => (
                          <div
                            className={
                              currentAudioDeviceId === device.deviceId
                                ? "call__controls__dropdown__item active"
                                : "call__controls__dropdown__item"
                            }
                            key={index}
                            onClick={() => onMicChange(device.deviceId)}
                          >
                            <div className="call__controls__dropdown__item__icon" />
                            <div className="call__controls__dropdown__item__text">
                              {device.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {sinkIdAvailable && (
                    <div
                      className={`active-call__top__controls__btn active-call__top__controls__btn--speaker ${muted ? "disabled" : ""}`}
                    >
                      <div
                        className="active-call__top__controls__btn__wrapper"
                        onClick={onMuteClick}
                      >
                        <div className="active-call__top__controls__btn__icon" />
                      </div>
                      <button
                        className="call__controls__btn-small"
                        onClick={() =>
                          setShowAudioOutputDropdown(!showAudioOutputDropdown)
                        }
                      />
                      {showAudioOutputDropdown &&
                        audioOutputDevices.length > 0 && (
                          <div className="call__controls__dropdown">
                            {audioOutputDevices.map((device, index) => (
                              <div
                                className={
                                  currentAudioOutputDeviceId === device.deviceId
                                    ? "call__controls__dropdown__item active"
                                    : "call__controls__dropdown__item"
                                }
                                key={index}
                                onClick={() =>
                                  onAudioOutputChange(device.deviceId)
                                }
                              >
                                <div className="call__controls__dropdown__item__icon" />
                                <div className="call__controls__dropdown__item__text">
                                  {device.label}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}
                  <div
                    className="active-call__top__controls__btn active-call__top__controls__btn--end-call"
                    onClick={finishCall}
                  >
                    <div className="active-call__top__controls__btn__icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`active-call__bottom ${clsMap[status]}`}>
            <div className="container">
              <div className="active-call__bottom__wrapper">
                {/* <div className="active-call__bottom__item">
                  <div className="active-call__bottom__item__label">
                    Product:
                  </div>
                  <div className="active-call__bottom__item__value">
                    Coffee Invest
                  </div>
                </div>
                <div className="active-call__bottom__item">
                  <div className="active-call__bottom__item__label">
                    Link:
                  </div>
                  <div className="active-call__bottom__item__value">
                    coffeeinvest.com/birzha-ebook/
                  </div>
                </div>
                <div className="active-call__bottom__item">
                  <div className="active-call__bottom__item__label">
                    Possible Problem:
                  </div>
                  <div className="active-call__bottom__item__value">
                    Download button is not working
                  </div>
                </div>
                <div className="active-call__bottom__info">
                  <div className="active-call__bottom__item">
                    <div className="active-call__bottom__item__label">
                      Country:
                    </div>
                    <div className="active-call__bottom__item__value">
                      Poland
                    </div>
                  </div>
                  <div className="active-call__bottom__item">
                    <div className="active-call__bottom__item__label">
                      Browser:
                    </div>
                    <div className="active-call__bottom__item__value">
                      Safari
                    </div>
                  </div>
                  <div className="active-call__bottom__item">
                    <div className="active-call__bottom__item__label">
                      OS:
                    </div>
                    <div className="active-call__bottom__item__value">
                      MacOS Sequoia 15.3
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
